import { put, call, all, take, Effect, select, takeLeading } from "redux-saga/effects";
import { userSlice } from "../reducer/user";
import { UserSetupState, userSetupSlice, UserSetupSubstep } from "../reducer/user_setup";
import { RootState } from "..";
import { RouterState, push } from "redux-first-history";
import { User } from "../../model/user";
import { locationApi } from "../../api/LocationApi";
import { extractSSN, maybeCleanInt, omitHyphen } from "../../utils/dataConverter";
import { PayloadAction } from "@reduxjs/toolkit";
import { authApi } from "../../api/AuthApi";
import { ExitType } from "../../constants/common"
import { fallbackPhoneNumber } from "../../model/personal_info";
import { ApiError, SerializedApiError } from "../../model/errors/errors";
import { SignUpRequestErrorModel } from "../../model/auth";

function* confirmStepSaga(): Generator<Effect, void, any> {
    const routerState = (yield select((state: RootState) => state.router)) as RouterState;

    const path = routerState.location!.pathname;
    switch (path) {
        case '/user/welcome-back':
            yield put(userSetupSlice.actions.confirmAddress());
            break;
        case '/user/update-address':
            yield put(userSetupSlice.actions.verifyAddress());
            break;
        case '/user/personal-info':
            yield put(userSetupSlice.actions.verifyPersonalInfo());
            break;
        case '/user/spouse-info':
            yield put(userSetupSlice.actions.verifySpouseInfo());
            break;
        case '/user/additional-info':
            yield put(userSetupSlice.actions.verifyAdditionalTaxInfo());
            break;
        case '/user/review':
            yield put(push('/upload/IRS1040'));
            break;
    }
}

/// Invoked when clicking Next on the Welcome Back screen
function* confirmAddressSaga(): Generator<Effect, void, any> {
    const userSetupState = (yield select((state: RootState) => state.userSetup)) as UserSetupState;
    
    /// If the user was confirming an existing address
    if (!userSetupState.infoAccurate) {
        /// Did not confirm, so go to address update
        yield put(push("/user/update-address"));
    } else {
        /// Confirmed previous address to be correct, proceed
        yield put(userSetupSlice.actions.addSubstepError(UserSetupSubstep.VerifyAddress));
        yield put(userSlice.actions.updateUserData({
            is_resident: true,
            is_resident_for_the_entire_year: true,
        }));
        yield put(push("/user/personal-info"));
        yield put(userSetupSlice.actions.setDisableNext(false));
    }
    return;
}

function* verifyAddressSaga(): Generator<Effect, void, any> {
    const user = (yield select((state: RootState) => state.user.data)) as User;
    // There's an edge case where a new registrant signs up and they moved addresses
    // during the tax year. In this case, they never are able to fill in
    // their phone number (since that occurs after this step) and the backend will error out 
    // and not be able to update user address-related info due to that missing datapoint.
    //
    // In this case we send a dummy phone number to backend just for this update call
    // there is an initial side-effect in the PersonalInfoForm component that cleans this up.
    const userPhoneNumber = user.phone_number ?? fallbackPhoneNumber;

    yield put(userSetupSlice.actions.setDisableNext(true));
    try {
        const validCityAddress = yield call(locationApi.validateAddress, user);
        yield put(userSlice.actions.updateUserData({
            phone_number: userPhoneNumber,
            is_resident: validCityAddress,
            is_resident_for_the_entire_year: validCityAddress, 
        }));

        if (!validCityAddress) {
            // Push to sorry page with part time exist code (client wanted to streamline)
            yield put(userSetupSlice.actions.setDisableNext(false));
            yield put(userSetupSlice.actions.setExitMessageType(ExitType.PartTimeResident));
            yield put(push("/user/sorry"));
            return;
        }

        // Persist user address to the backend only for valid
        // addresses that meet GeoFencing requirements. Persisting
        // non-valid addresses could make it very weird on subsequent
        // log-ins for future tax years since the user can just confirm that they lived at the invalid
        // address and file their taxes even though they are not a valid resident.
        const updatedUser = (yield select((state: RootState) => state.user.data)) as User;
        yield put(userSlice.actions.updateUser(updatedUser));

        // Wait for backend user update to conclude before
        // pushing to PersonalInfoForm. This is so that component can
        // perform some clean-up regarding new user's phone numbers.
        yield take([
            userSlice.actions.updateUserSuccess.type,
            userSlice.actions.updateUserError.type
        ]);

        yield put(push("/user/personal-info"));
        yield put(userSetupSlice.actions.setDisableNext(false));
    } catch (error) {
        console.log(error);
        yield put(userSetupSlice.actions.addSubstepError(UserSetupSubstep.VerifyAddress));
        yield put(userSetupSlice.actions.setDisableNext(false));
        yield put(userSetupSlice.actions.setExitMessageType(ExitType.UnknownAddressVerificationError));
        yield put(push("/user/sorry"));
        return;
    }
}

function* verifyPersonalInfoSaga(): Generator<Effect, void, any> {
    const user = (yield select((state: RootState) => state.user.data)) as User;

    if (user.phone_number) {
        if ((maybeCleanInt(user.phone_number) as string).length !== 10) {
            yield put(userSetupSlice.actions.addSubstepError(UserSetupSubstep.PersonalInfo));
            yield put(userSetupSlice.actions.setPInfoPhoneNoValidationError(
                "Current phone number is invalid"
            ));
            return;
        }
        yield put(userSetupSlice.actions.setPInfoPhoneNoValidationError(""));
        yield put(userSetupSlice.actions.setPInfoSsnValidationError(""));
        yield put(userSlice.actions.updateUser(
            (yield select((state: RootState) => state.user.data))
        ));
        if (user.is_jointfiling) {
            yield put(push('/user/spouse-info'));
        } else {
            yield put(push('/user/additional-info'));
        }
    } else {
        yield put(userSetupSlice.actions.addSubstepError(UserSetupSubstep.PersonalInfo));
        yield put(userSetupSlice.actions.setPInfoPhoneNoValidationError(
            !user.phone_number ? "Current phone number is required" : ""
        ));
    }
}

function* verifySpouseInfoSaga(): Generator<Effect, void, any> {
    const user = (yield select((state: RootState) => state.user.data)) as User;
    const userSetupState = (yield select((state: RootState) => state.userSetup)) as UserSetupState;

    if (user.spouse_social_security) {
        if (!user.spouse_social_security.startsWith('XXX-XX-')) {
            if ((maybeCleanInt(user.spouse_social_security) as string).length !== 9) {
                yield put(userSetupSlice.actions.setSpouseSsnValidationError("Social Security is invalid."));
                return;
            }

            if (omitHyphen(user.spouse_social_security) === omitHyphen(user.social_security)) {
                yield put(userSetupSlice.actions.setSpouseSsnValidationError(
                    "Social Security matches partner's"
                ));
                return;
            }
        }

        yield put(userSetupSlice.actions.setSpouseSsnValidationError(""));
        yield put(userSlice.actions.updateUser(
            (yield select((state: RootState) => state.user.data))
        ));
        yield put(push('/user/additional-info'));
      } else {
        yield put(userSlice.actions.updateUser(
            (yield select((state: RootState) => state.user.data))
        ));
        yield put(userSetupSlice.actions.setSpouseSsnValidationError(
          !user.spouse_social_security
            ? "Spouse’s social security is required"
            : ""
        ));
      }
}

function* verifyAdditionalTaxInfoSaga(): Generator<Effect, void, any> {
    const user = (yield select((state: RootState) => state.user.data)) as User;

    if (user.is_sch_e_filed && user.is_business_sale_filed) {
        yield put(userSetupSlice.actions.setExitMessageType(ExitType.FiledSchEAnd4797));
        yield put(push("/user/sorry"));
    } else if (user.is_sch_e_filed) {
        yield put(userSetupSlice.actions.setExitMessageType(ExitType.FiledSchE));
        yield put(push("/user/sorry"));
    } else if (user.is_business_sale_filed) {
        yield put(userSetupSlice.actions.setExitMessageType(ExitType.Filed4797));
        yield put(push("/user/sorry"));
    } else {
        yield put(userSlice.actions.updateUserComplete(user));
    }
}

function* precogValidateSSNForSpouse({ payload } : PayloadAction<string>) {
    try {
      yield call(authApi.signUpPrecogValidate, {
        username: '',
        password: '',
        password_confirmation: '',
        social_security: extractSSN(payload),
        email: '',
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        zip: '',
        captcha_value: '',
      },
      ['social_security']);

      yield put(userSetupSlice.actions.setSpouseSsnValidationError(''));
      yield put(userSetupSlice.actions.setDisableNext(false));
      
    } catch (error: SerializedApiError<SignUpRequestErrorModel> | any) {
      const err = ApiError.deserialize<SignUpRequestErrorModel>(error);
      const data = err?.data;
      const ssnErrors = data?.errors['social_security'];
      if (ssnErrors) {
        yield put(userSetupSlice.actions.setSpouseSsnValidationError(ssnErrors[0]));
      } else {
        yield put(userSetupSlice.actions.setSpouseSsnValidationError(String(error)));
      }
    } finally {
      yield put(userSetupSlice.actions.setDisableNext(false));
    }
  }

export default function* userSetupSaga() {
    yield all([takeLeading(userSetupSlice.actions.confirmStep, confirmStepSaga)]);
    yield all([takeLeading(userSetupSlice.actions.confirmAddress, confirmAddressSaga)]);
    yield all([takeLeading(userSetupSlice.actions.verifyAddress, verifyAddressSaga)]);
    yield all([takeLeading(userSetupSlice.actions.verifyPersonalInfo, verifyPersonalInfoSaga)]);
    yield all([takeLeading(userSetupSlice.actions.verifySpouseInfo, verifySpouseInfoSaga)]);
    yield all([takeLeading(userSetupSlice.actions.validateSSN.type, precogValidateSSNForSpouse)]);
    yield all([takeLeading(userSetupSlice.actions.verifyAdditionalTaxInfo, verifyAdditionalTaxInfoSaga)]);
}
